import type { RowData } from "@tanstack/react-table";

import { MAX_PAGE_LIMIT } from "@/lib/api/http";
import type { QueryParams } from "@/lib/api/http";

/** Cursor-paginated list envelope (`next_cursor: null` means last page). */
export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
}

/**
 * Server-side sort. `key` is the column id (accessorKey unless the column
 * sets an explicit `id`) and must match a sort key the endpoint understands.
 * `null` = server default ordering.
 */
export type SortState = { key: string; dir: "asc" | "desc" } | null;

/** Arguments a cursor-paginated list fetcher receives for one page. */
export interface CursorQueryArgs {
  /** Opaque cursor from the previous page's `next_cursor`; omit for page one. */
  cursor?: string | null;
  /** Page size; defaults to DEFAULT_PAGE_LIMIT, capped at MAX_PAGE_LIMIT (200). */
  limit?: number;
  sort?: SortState;
  /** TanStack Query cancellation signal; transports must forward it to fetch. */
  signal?: AbortSignal;
}

export const DEFAULT_PAGE_LIMIT = 50;

/**
 * Serialize CursorQueryArgs into buildQuery-ready params using the wire
 * conventions: `sort=<key>:<dir>`, `limit` defaulted to 50 and clamped to
 * 1..200, `cursor` omitted on the first page.
 *
 * Usage: `apiFetch<CursorPage<T>>("/orders", { query: toListParams(args) })`.
 */
export function toListParams(args: CursorQueryArgs = {}): QueryParams {
  const params: QueryParams = {};
  if (args.cursor !== undefined && args.cursor !== null) {
    params.cursor = args.cursor;
  }
  const limit = args.limit ?? DEFAULT_PAGE_LIMIT;
  params.limit = Math.min(Math.max(1, Math.trunc(limit)), MAX_PAGE_LIMIT);
  if (args.sort) {
    params.sort = `${args.sort.key}:${args.sort.dir}`;
  }
  return params;
}

/**
 * Column meta the DataTable understands. Declared via TanStack's ColumnMeta
 * augmentation so `meta: { align: "end", numeric: true }` type-checks on any
 * ColumnDef in the app.
 */
export interface DataTableColumnMeta {
  /** Cell/header alignment. Defaults to "start"; `numeric` implies "end". */
  align?: "start" | "end";
  /**
   * Tabular figures + LTR isolation for numeric content (quantities, amounts,
   * ids). Numeric cells align "end" unless `align` says otherwise.
   */
  numeric?: boolean;
}

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-object-type -- must mirror the library's type parameters; interface required for declaration merging
  interface ColumnMeta<TData extends RowData, TValue> extends DataTableColumnMeta {}
}
